/* iFungi Buy Box Cart Transport — market-aware, independently protected attempts.
 * 2026-10-11: bounded negative reconciliation replaces the permanent global lock.
 * Never retries a POST automatically. Three fresh unchanged/absent reads over 8 s
 * permit a shopper-initiated retry; this is a recovery policy, not server idempotency.
 * 2026-10-16 (late-commit hardening): after an add whose outcome is unknown (timeout, dropped
 * connection, 429 or 5xx), "not in the cart" is not accepted as final until 60 s after the add
 * was sent; until then the attempt stays protected (unresolved / View cart). A retry of the same
 * line within 10 minutes of such an attempt re-reads the cart first; if the earlier add has since
 * appeared, nothing is sent and the add is reported as done. If that read is ambiguous (the line
 * changed by a different amount), the attempt becomes a protected pending record again (View cart /
 * the cart page's "couldn't confirm" notice) instead of being forgotten.
 * Limit: Shopify's cart API has no idempotency key, so a timed-out write that commits right after
 * the retry-time read can still produce a second line. The hold and the re-read make this much less
 * likely; they cannot rule it out.
 */
(function () {
  'use strict';
  if (window.iFungiBuyboxCart) return;

  var SNAPSHOT_MAX_AGE_MS = 45000;
  var READ_TIMEOUT_MS = 20000;
  var RECONCILE_READ_TIMEOUT_MS = 3500;
  var SNAPSHOT_WAIT_BEFORE_ADD_MS = 1500;
  var ADD_TIMEOUT_MS = 20000;
  var RECHECK_AFTER_MS = 120000;
  var NEGATIVE_READ_GAP_MS = 4000;
  var NEGATIVE_READ_COUNT = 3;
  var LATE_COMMIT_GUARD_MS = 60000;
  var RECENT_ABSENT_KEY = 'ifungi_add_recent_absent_v1';
  var RECENT_ABSENT_TTL_MS = 600000;
  var UNRESOLVED_KEY = 'ifungi_add_unresolved_v3';
  var LEGACY_KEY = 'ifungi_add_unresolved_v2';
  var snapshot = null, snapshotPromise = null;
  var pendingInMemory = [], memoryOnly = false;
  var activeTransports = {}, resolutions = {}, productVariants = {};
  var attemptSequence = 0;
  var recentAbsentMemory = [];

  function asError(response, payload) {
    var detail = payload && (payload.description || payload.message || payload.errors);
    var error = new Error(typeof detail === 'string' ? detail : 'Cart request failed');
    error.status = response.status;
    error.code = payload && (payload.status || payload.code) ? String(payload.status || payload.code) : String(response.status);
    error.payload = payload || null;
    return error;
  }
  function jsonResponse(response) {
    return response.text().then(function (text) {
      var payload = null;
      try { payload = text ? JSON.parse(text) : null; } catch (e) {}
      if (!response.ok) throw asError(response, payload);
      if (!payload) throw new Error('Shopify returned an empty cart response');
      return payload;
    });
  }
  function textResponse(response) {
    if (!response.ok) throw new Error('Unable to refresh the cart drawer');
    return response.text().then(function (html) {
      if (!html) throw new Error('Shopify returned an empty cart drawer');
      return html;
    });
  }
  function cartRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }
  function lineKey(variantId, sellingPlan, properties) {
    var key = String(variantId) + '|' + (sellingPlan ? String(sellingPlan) : '');
    var pairs = Object.keys(properties || {}).filter(function (name) { return properties[name] !== null && properties[name] !== ''; }).sort().map(function (name) { return [name, String(properties[name])]; });
    return pairs.length ? key + '|' + JSON.stringify(pairs) : key;
  }
  function cartLines(cart) {
    if (!cart || !Array.isArray(cart.items)) throw new Error('Cart read did not contain usable items');
    var lines = {};
    cart.items.forEach(function (item) {
      if (!item || !item.variant_id || !isFinite(Number(item.quantity)) || Number(item.quantity) < 0) throw new Error('Cart line did not contain usable identity and quantity');
      var plan = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan ? item.selling_plan_allocation.selling_plan.id : '';
      var key = lineKey(item.variant_id, plan, item.properties);
      lines[key] = (lines[key] || 0) + Number(item.quantity || 0);
    });
    return lines;
  }
  function fetchCartBounded(root, timeout) {
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timer;
    var deadline = new Promise(function (resolve, reject) {
      timer = setTimeout(function () {
        if (controller) { try { controller.abort(); } catch (e) {} }
        reject(new Error('Cart read timed out'));
      }, timeout || READ_TIMEOUT_MS);
    });
    var read = Promise.resolve().then(function () {
      return fetch(root + 'cart.js', { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' }, signal: controller ? controller.signal : undefined });
    }).then(jsonResponse).then(function (cart) { cartLines(cart); return cart; });
    return Promise.race([read, deadline]).then(function (cart) { clearTimeout(timer); return cart; }, function (error) { clearTimeout(timer); throw error; });
  }
  function storages() {
    var list = [];
    try { if (window.sessionStorage) list.push(window.sessionStorage); } catch (error) {}
    try { if (window.localStorage) list.push(window.localStorage); } catch (error) {}
    return list;
  }
  function inferProduct(items) {
    var label = String(items[0] && items[0].label || '').toLowerCase();
    if (label.indexOf('life force') !== -1) return 'lf7';
    if (label.indexOf('petshrooms') !== -1) return 'ps7';
    var first = String(items[0] && items[0].variantId || '');
    var products = Object.keys(productVariants);
    for (var i = 0; i < products.length; i++) {
      if (productVariants[products[i]][first]) return products[i];
    }
    return '';
  }
  function validRecord(entry) {
    return entry && Array.isArray(entry.items) && entry.items.length && entry.items.every(function (item) {
      return item && item.variantId && Number(item.quantity || 1) > 0;
    });
  }
  function persist(records) {
    pendingInMemory = records;
    memoryOnly = true;
    var stores = storages(), saved = false;
    var raw = JSON.stringify({ version: 3, entries: records });
    /* Write an explicit empty envelope too: readable storage is authoritative on Back.
       Remove fallback copies only AFTER a successful write; never erase the sole copy. */
    for (var i = 0; i < stores.length; i++) {
      try {
        stores[i].setItem(UNRESOLVED_KEY, raw);
        memoryOnly = false; saved = true;
        for (var j = 0; j < stores.length; j++) {
          try { stores[j].removeItem(LEGACY_KEY); if (j !== i) stores[j].removeItem(UNRESOLVED_KEY); } catch (ignore) {}
        }
        break;
      } catch (error) {}
    }
    return saved;
  }
  function readRecords() {
    if (memoryOnly) return pendingInMemory;
    var stores = storages(), readable = false;
    for (var i = 0; i < stores.length; i++) {
      try {
        var raw = stores[i].getItem(UNRESOLVED_KEY);
        readable = true;
        if (!raw) continue;
        var envelope = JSON.parse(raw);
        if (envelope && envelope.version === 3 && Array.isArray(envelope.entries)) {
          pendingInMemory = envelope.entries.filter(validRecord);
          return pendingInMemory;
        }
      } catch (error) {}
    }
    /* Migrate v2 without deleting uncertainty or inventing a pre-add snapshot. */
    for (var k = 0; k < stores.length; k++) {
      try {
        var legacyRaw = stores[k].getItem(LEGACY_KEY);
        if (!legacyRaw) continue;
        var legacy = JSON.parse(legacyRaw);
        if (validRecord(legacy)) {
          legacy.id = 'legacy-' + String(legacy.at || 0) + '-' + String(legacy.items[0].variantId);
          legacy.productKey = inferProduct(legacy.items);
          legacy.phase = 'unknown';
          legacy.recheckAt = Date.now();
          persist([legacy]);
          return [legacy];
        }
      } catch (error) {}
    }
    if (readable && !memoryOnly) pendingInMemory = [];
    return pendingInMemory;
  }
  function matchesProduct(entry, productKey) {
    if (!productKey) return true;
    var owner = entry.productKey || inferProduct(entry.items);
    /* An unclassifiable legacy attempt remains conservative/cart-visible, never lost. */
    return !owner || owner === productKey;
  }
  function getPending(productKey) {
    return readRecords().filter(function (entry) { return matchesProduct(entry, productKey); });
  }
  function registerProduct(productKey, variants) {
    var known = productVariants[productKey] || {};
    (variants || []).forEach(function (variantId) { known[String(variantId)] = true; });
    productVariants[productKey] = known;
  }
  function findRecord(id) {
    return readRecords().filter(function (entry) { return entry.id === id; })[0] || null;
  }
  function updateRecord(id, values) {
    var records = readRecords();
    var changed = false;
    records.forEach(function (entry) {
      if (entry.id !== id) return;
      Object.keys(values).forEach(function (key) { entry[key] = values[key]; });
      changed = true;
    });
    if (changed) persist(records);
  }
  function clearAttempts(ids) {
    var records = readRecords().filter(function (entry) { return ids.indexOf(entry.id) === -1; });
    persist(records);
  }
  function clearUnresolved(productKey) {
    clearAttempts(getPending(productKey).map(function (entry) { return entry.id; }));
  }
  /* 2026-10-16: attempts closed as "absent" after an unknown outcome are remembered for 10 minutes
     (with their pre-add cart counts), so a retry of the same line can first check whether Shopify
     finished the earlier add after all. Session storage only; never shown and never blocks on its own. */
  function readRecentAbsent() {
    var list = recentAbsentMemory;
    try {
      var raw = window.sessionStorage ? window.sessionStorage.getItem(RECENT_ABSENT_KEY) : null;
      if (raw) { var parsed = JSON.parse(raw); if (Array.isArray(parsed)) list = parsed; }
    } catch (error) {}
    var now = Date.now();
    return (list || []).filter(function (ghost) { return ghost && Array.isArray(ghost.items) && ghost.items.length && ghost.before && Number(ghost.until) > now; });
  }
  function writeRecentAbsent(list) {
    recentAbsentMemory = list;
    try {
      if (window.sessionStorage) {
        if (list.length) window.sessionStorage.setItem(RECENT_ABSENT_KEY, JSON.stringify(list));
        else window.sessionStorage.removeItem(RECENT_ABSENT_KEY);
      }
    } catch (error) {}
  }
  function rememberAbsent(entry) {
    /* Without a pre-add snapshot a later read can't tell a late add from a line that was already there. */
    if (!entry || !entry.before) return;
    var list = readRecentAbsent().filter(function (ghost) { return ghost.id !== entry.id; });
    list.push({ id: entry.id, at: entry.at, items: entry.items, before: entry.before, productKey: entry.productKey, until: Date.now() + RECENT_ABSENT_TTL_MS });
    writeRecentAbsent(list);
  }
  function writeAttempt(items, before, productKey) {
    var now = Date.now();
    var entry = {
      id: String(now) + '-' + String(++attemptSequence) + '-' + Math.random().toString(36).slice(2, 10),
      at: now, productKey: productKey || inferProduct(items), items: items,
      before: before ? before.lines : null, phase: 'inflight', recheckAt: now + RECHECK_AFTER_MS
    };
    var records = readRecords().slice(); records.push(entry); persist(records);
    return entry;
  }
  function classify(entry, cart) {
    var after = cartLines(cart), expected = {};
    entry.items.forEach(function (item) {
      var key = lineKey(item.variantId, item.sellingPlan, item.properties);
      expected[key] = (expected[key] || 0) + Number(item.quantity || 1);
    });
    var keys = Object.keys(expected);
    if (!entry.before) {
      return { status: keys.every(function (key) { return !(after[key] || 0); }) ? 'absent' : 'unresolved', cart: cart, after: after };
    }
    var allAdded = keys.every(function (key) { return (after[key] || 0) === (Number(entry.before[key]) || 0) + expected[key]; });
    if (allAdded) return { status: 'added', cart: cart, after: after };
    var unchanged = keys.every(function (key) { return (after[key] || 0) === (Number(entry.before[key]) || 0); });
    return { status: unchanged ? 'absent' : 'partial', cart: cart, after: after };
  }
  function wait(ms) { return new Promise(function (resolve) { setTimeout(resolve, ms); }); }
  function reconcileAttempt(entry, definitiveRefusal) {
    if (resolutions[entry.id]) return resolutions[entry.id];
    var negativeReads = 0;
    /* A page restored while the POST is active must not classify a temporary unchanged
       cart as failure. Across reload wait out the original transport deadline first. */
    var notBefore = entry.phase === 'inflight' ? Number(entry.at || Date.now()) + ADD_TIMEOUT_MS : 0;
    /* 2026-10-16: Shopify can still finish a timed-out add. Unless Shopify refused it outright,
       "absent" is final only from 60 s after the add was sent. */
    var guardUntil = definitiveRefusal ? 0 : Number(entry.at || 0) + LATE_COMMIT_GUARD_MS;
    function step() {
      var current = findRecord(entry.id);
      if (!current) return Promise.resolve({ status: 'none' });
      if (activeTransports[entry.id]) return Promise.resolve({ status: 'unresolved' });
      return fetchCartBounded(cartRoot(), RECONCILE_READ_TIMEOUT_MS).then(function (cart) {
        current = findRecord(entry.id);
        if (!current) return { status: 'none' };
        var result = classify(current, cart);
        if (result.status === 'added') {
          clearAttempts([entry.id]); snapshot = { at: Date.now(), lines: result.after }; return result;
        }
        if (result.status === 'partial') {
          updateRecord(entry.id, { phase: 'partial', observedPartial: true, recheckAt: Date.now() + RECHECK_AFTER_MS });
          result.status = 'unresolved'; return result;
        }
        if (result.status !== 'absent' || current.observedPartial || current.phase === 'partial') {
          updateRecord(entry.id, { recheckAt: Date.now() + RECHECK_AFTER_MS });
          result.status = 'unresolved'; return result;
        }
        negativeReads++;
        if (definitiveRefusal || negativeReads >= NEGATIVE_READ_COUNT) {
          if (!definitiveRefusal && Date.now() < guardUntil) {
            /* Too early to rule out a late add: keep the attempt protected and look again later. */
            updateRecord(entry.id, { recheckAt: guardUntil });
            result.status = 'unresolved'; return result;
          }
          if (!definitiveRefusal) rememberAbsent(current);
          clearAttempts([entry.id]); snapshot = { at: Date.now(), lines: result.after }; return result;
        }
        return wait(NEGATIVE_READ_GAP_MS).then(step);
      }, function () {
        /* Approved 2026-10-17: a failed READ does not add another two-minute wait.
           The original 60-second write guard is unchanged; once it expires,
           a fresh successful read must still confirm absence before any retry. */
        updateRecord(entry.id, { recheckAt: Math.max(guardUntil, Date.now() + NEGATIVE_READ_GAP_MS) });
        return { status: 'unreadable' };
      });
    }
    var promise = (notBefore > Date.now() ? wait(notBefore - Date.now()) : Promise.resolve()).then(step);
    resolutions[entry.id] = promise.then(function (result) { delete resolutions[entry.id]; return result; }, function (error) { delete resolutions[entry.id]; throw error; });
    return resolutions[entry.id];
  }
  function resolveUnresolved(productKey) {
    var entries = getPending(productKey);
    if (!entries.length) return Promise.resolve('none');
    return Promise.all(entries.map(function (entry) { return reconcileAttempt(entry, false); })).then(function (results) {
      if (results.some(function (result) { return result.status === 'unreadable'; })) return 'unreadable';
      if (results.some(function (result) { return result.status === 'unresolved'; })) return 'unresolved';
      if (results.some(function (result) { return result.status === 'added'; })) return 'added';
      if (results.some(function (result) { return result.status === 'absent'; })) return 'absent';
      return 'none';
    });
  }
  function isRecheckDue(productKey) {
    return getPending(productKey).some(function (entry) { return !entry.recheckAt || Date.now() >= Number(entry.recheckAt); });
  }
  function primeSnapshot(force) {
    if (!force && snapshot && Date.now() - snapshot.at < SNAPSHOT_MAX_AGE_MS) return Promise.resolve(snapshot);
    if (snapshotPromise) return snapshotPromise;
    snapshotPromise = fetchCartBounded(cartRoot()).then(function (cart) {
      snapshot = { at: Date.now(), lines: cartLines(cart) }; snapshotPromise = null; return snapshot;
    }, function () { snapshotPromise = null; return null; });
    return snapshotPromise;
  }
  function snapshotForAdd() {
    if (snapshot && Date.now() - snapshot.at < SNAPSHOT_MAX_AGE_MS) return Promise.resolve(snapshot);
    var pending = snapshotPromise || primeSnapshot(true), timer;
    var shortWait = new Promise(function (resolve) { timer = setTimeout(function () { resolve(null); }, SNAPSHOT_WAIT_BEFORE_ADD_MS); });
    return Promise.race([pending, shortWait]).then(function (snap) { clearTimeout(timer); return snap && snap.lines ? snap : null; }, function () { clearTimeout(timer); return null; });
  }
  function isOutcomeUnknown(error) {
    if (!error) return false;
    var status = Number(error.status);
    return error.status === undefined || status === 0 || status === 408 || status === 429 || status >= 500;
  }
  /* 2026-10-13: never infer "nothing added" from all 4xx replies. Shopify can
     cap adds with 422 and apply a mutation despite a section-render 400.
     A plain 422 rejecting ONE requested unit of ONE variant cannot be a
     partially filled add. Explicit partial-result payloads still reconcile. */
  function isSingleUnitRefusal(error, entry) {
    if (Number(error && error.status) !== 422 || !entry || entry.items.length !== 1 || Number(entry.items[0].quantity || 1) !== 1) return false;
    var payload = error.payload || {};
    if (payload.items || payload.item || payload.quantity || payload.sections) return false;
    var message = String(error.message || '');
    return !/only\s+\d|(?:items?|articles?).*(?:added|ajout)|(?:added|ajout).*(?:items?|articles?)/i.test(message);
  }
  function pendingError(message) {
    var error = new Error(message); error.status = 'unknown'; error.unresolved = true; return error;
  }
  function settleAdd(transportPromise, entry) {
    return transportPromise.then(function (result) {
      delete activeTransports[entry.id]; snapshot = null; clearAttempts([entry.id]); return result;
    }, function (error) {
      delete activeTransports[entry.id]; snapshot = null;
      updateRecord(entry.id, { phase: 'unknown', failedAt: Date.now() });
      if (isSingleUnitRefusal(error, entry)) {
        clearAttempts([entry.id]);
        error.retryable = true;
        throw error;
      }
      return reconcileAttempt(findRecord(entry.id) || entry, !isOutcomeUnknown(error)).then(function (result) {
        if (result.status === 'added') return result.cart;
        if (result.status === 'absent') { error.retryable = true; throw error; }
        var unresolved = pendingError('Cart add outcome unresolved (' + result.status + ')');
        unresolved.cause = error;
        /* Preserve a received refusal even when a bundle/capped add must stay
           protected until its exact cart outcome can be read. */
        if (!isOutcomeUnknown(error)) unresolved.refusalMessage = String(error.message || '');
        throw unresolved;
      });
    });
  }
  function overlap(entry, items) {
    return entry.items.some(function (pending) {
      return items.some(function (item) { return lineKey(item.variantId, item.sellingPlan, item.properties) === lineKey(pending.variantId, pending.sellingPlan, pending.properties); });
    });
  }
  function protectThenAdd(items, options, send) {
    var productKey = options.productKey || inferProduct(items);
    var existing = readRecords().filter(function (entry) { return matchesProduct(entry, productKey) || overlap(entry, items); });
    if (existing.length) return Promise.reject(pendingError('A previous add for this product or bundled line still needs a cart check'));
    var ghosts = readRecentAbsent().filter(function (ghost) { return overlap(ghost, items); });
    var lateCheck = !ghosts.length ? Promise.resolve(null) : fetchCartBounded(cartRoot(), RECONCILE_READ_TIMEOUT_MS).then(function (cart) {
      var ids = ghosts.map(function (ghost) { return ghost.id; });
      var statuses = ghosts.map(function (ghost) { return classify(ghost, cart).status; });
      var ambiguous = ghosts.filter(function (ghost, i) { return statuses[i] !== 'added' && statuses[i] !== 'absent'; });
      if (ambiguous.length) {
        /* 2026-10-16 (retest #3): don't forget an attempt whose outcome this read can't settle. It
           goes back into the protected pending store, so the next add is refused until the cart page's
           check or the shopper's own "My cart looks right" settles it. */
        var records = readRecords().slice(), known = records.map(function (entry) { return entry.id; });
        ambiguous.forEach(function (ghost) {
          if (known.indexOf(ghost.id) !== -1) return;
          records.push({ id: ghost.id, at: ghost.at || Date.now(), productKey: ghost.productKey || inferProduct(ghost.items), items: ghost.items,
            before: ghost.before, phase: 'partial', observedPartial: true, recheckAt: Date.now() + RECHECK_AFTER_MS });
        });
        persist(records);
      }
      /* Settled or now protected: either way the short-term memory has done its job. */
      writeRecentAbsent(readRecentAbsent().filter(function (ghost) { return ids.indexOf(ghost.id) === -1; }));
      snapshot = { at: Date.now(), lines: cartLines(cart) };
      if (ambiguous.length) throw pendingError('An earlier add for this line may have completed; check the cart first');
      if (statuses.indexOf('added') !== -1) return { lateAdded: true, cart: cart };
      return null;
    }, function () {
      throw pendingError('The cart could not be checked before adding again');
    });
    return lateCheck.then(function (late) {
      /* The earlier add did reach the cart after all: report success without sending another. */
      if (late && late.lateAdded) return late.cart;
      return snapshotForAdd().then(function (before) {
      /* Recheck after the asynchronous snapshot wait: another click must not overwrite an attempt. */
      var conflicts = readRecords().some(function (entry) { return matchesProduct(entry, productKey) || overlap(entry, items); });
      if (conflicts) throw pendingError('An add for this product is already pending');
      var entry = writeAttempt(items, before, productKey);
      activeTransports[entry.id] = true;
      var transport = Promise.resolve().then(send);
      return settleAdd(transport, entry);
      });
    });
  }
  function xhrAddLine(url, formData) {
    return new Promise(function (resolve, reject) {
      var request = new XMLHttpRequest(); request.open('POST', url, true);
      request.withCredentials = true; request.timeout = ADD_TIMEOUT_MS;
      request.setRequestHeader('X-Requested-With', 'XMLHttpRequest'); request.setRequestHeader('Accept', 'application/json');
      request.onload = function () {
        var payload = null;
        try { payload = request.responseText ? JSON.parse(request.responseText) : null; } catch (e) {}
        var response = { ok: request.status >= 200 && request.status < 300, status: request.status };
        if (!response.ok) return reject(asError(response, payload));
        if (!payload) return reject(new Error('Shopify returned an empty cart response'));
        resolve(payload);
      };
      request.onerror = function () { var error = new Error('Unable to add item to cart'); error.status = 0; reject(error); };
      request.ontimeout = function () { var error = new Error('Cart add request timed out'); error.status = 408; reject(error); };
      request.send(formData);
    });
  }
  function addLine(routes, options) {
    var formData = new FormData(); formData.set('id', options.variantId); formData.set('quantity', options.quantity || 1);
    if (options.sellingPlan) formData.set('selling_plan', options.sellingPlan);
    Object.keys(options.properties || {}).forEach(function (name) { formData.set('properties[' + name + ']', options.properties[name]); });
    if (options.sections && options.sections.length) formData.set('sections', options.sections.join(','));
    if (options.sectionsUrl) formData.set('sections_url', options.sectionsUrl);
    var items = [{ variantId: options.variantId, quantity: options.quantity || 1, sellingPlan: options.sellingPlan, properties: options.properties, label: options.label }];
    return protectThenAdd(items, options, function () { return xhrAddLine(routes.cart_add_url, formData); });
  }
  function addLines(routes, options) {
    var items = options && Array.isArray(options.items) ? options.items : [];
    if (!items.length) return Promise.reject(new Error('No cart items were supplied'));
    var payload = { items: items.map(function (item) {
      var line = { id: item.variantId, quantity: item.quantity || 1 };
      if (item.sellingPlan) line.selling_plan = item.sellingPlan;
      if (item.properties) line.properties = item.properties;
      return line;
    }) };
    if (options.sections && options.sections.length) payload.sections = options.sections;
    if (options.sectionsUrl) payload.sections_url = options.sectionsUrl;
    return protectThenAdd(items, options, function () {
      var controller = typeof AbortController === 'function' ? new AbortController() : null, timer;
      var deadline = new Promise(function (resolve, reject) {
        timer = setTimeout(function () {
          if (controller) { try { controller.abort(); } catch (e) {} }
          var error = new Error('Cart add request timed out'); error.status = 408; reject(error);
        }, ADD_TIMEOUT_MS);
      });
      var transport = Promise.resolve().then(function () {
        return fetch(routes.cart_add_url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, credentials: 'same-origin', body: JSON.stringify(payload), signal: controller ? controller.signal : undefined });
      }).then(jsonResponse);
      return Promise.race([transport, deadline]).then(function (result) { clearTimeout(timer); return result; }, function (error) { clearTimeout(timer); throw error; });
    });
  }
  function fetchCart(root) {
    return fetch(root + 'cart.js', { credentials: 'same-origin', cache: 'no-store' }).then(jsonResponse);
  }
  function fetchCartAndDrawer(root, sectionUrl) {
    return Promise.all([
      fetchCart(root),
      fetch(sectionUrl, { headers: { Accept: 'text/html' }, credentials: 'same-origin', cache: 'no-store' }).then(textResponse)
    ]).then(function (results) { return { cart: results[0], drawerHtml: results[1] }; });
  }
  ['pointerdown', 'touchstart', 'focusin', 'keydown'].forEach(function (name) {
    document.addEventListener(name, function () { primeSnapshot(false); }, { capture: true, passive: true });
  });
  window.addEventListener('pageshow', function (event) { if (event.persisted) { snapshot = null; primeSnapshot(true); } });
  window.iFungiBuyboxCart = {
    addLine: addLine, addLines: addLines, fetchCart: fetchCart, fetchCartAndDrawer: fetchCartAndDrawer,
    primeSnapshot: primeSnapshot, registerProduct: registerProduct, getPending: getPending,
    hasUnresolved: function (productKey) { return !!getPending(productKey).length; },
    resolveUnresolved: resolveUnresolved, isRecheckDue: isRecheckDue,
    clearUnresolved: clearUnresolved, clearAttempts: clearAttempts
  };
})();
